export const PHONE_OTP_LENGTH = 4;

const BULK_INSERT_INPUT_TYPES = new Set([
  "insertFromPaste",
  "insertReplacementText",
  "insertFromAutocomplete",
  "insertFromDrop",
]);

function toDigits(value: string): string {
  return value.replace(/\D/g, "");
}

export function normalizePhoneDigits(value: string): string {
  const digits = toDigits(value);

  if (!digits) {
    return "";
  }

  if (digits.startsWith("8")) {
    return `7${digits.slice(1, 11)}`;
  }

  if (digits.startsWith("7")) {
    return digits.slice(0, 11);
  }

  if (digits.length <= 10) {
    return `7${digits.slice(0, 10)}`;
  }

  return digits.slice(0, 11);
}

/** Normalization for paste / iOS autofill (may omit leading country digit). */
export function normalizePhoneDigitsFromBulkInsert(value: string): string {
  const digits = toDigits(value);

  if (!digits) {
    return "";
  }

  if (digits.startsWith("8")) {
    return `7${digits.slice(1, 11)}`;
  }

  if (digits.length === 11 && digits.startsWith("7")) {
    return digits;
  }

  if (digits.length > 11) {
    return `7${digits.slice(-10)}`;
  }

  if (digits.length === 10 && digits.startsWith("7")) {
    return `7${digits}`;
  }

  return `7${digits.slice(0, 10)}`;
}

export function isBulkNumericInsert(event: Event): boolean {
  if (!(event instanceof InputEvent)) {
    return false;
  }

  if (event.inputType && BULK_INSERT_INPUT_TYPES.has(event.inputType)) {
    return true;
  }

  const insertedDigits = (event.data ?? "").replace(/\D/g, "");
  return insertedDigits.length > 1;
}

/** @deprecated Use {@link isBulkNumericInsert} */
export function isPhoneInputBulkInsert(event: Event): boolean {
  return isBulkNumericInsert(event);
}

export function isOtpInputBulkInsert(event: Event): boolean {
  return isBulkNumericInsert(event);
}

export function formatPhoneInput(value: string, options?: { fromBulkInsert?: boolean }): string {
  const digits = options?.fromBulkInsert ? normalizePhoneDigitsFromBulkInsert(value) : normalizePhoneDigits(value);
  if (!digits) {
    return "";
  }

  const local = digits.slice(1);
  const part1 = local.slice(0, 3);
  const part2 = local.slice(3, 6);
  const part3 = local.slice(6, 8);
  const part4 = local.slice(8, 10);

  let formatted = "+7";

  if (part1) {
    formatted += ` (${part1}`;
    if (part1.length === 3) {
      formatted += ")";
    }
  }

  if (part2) {
    formatted += ` ${part2}`;
  }

  if (part3) {
    formatted += `-${part3}`;
  }

  if (part4) {
    formatted += `-${part4}`;
  }

  return formatted;
}

export function resolvePhoneInputValue(rawValue: string, event?: Event): string {
  const fromBulkInsert = event ? isPhoneInputBulkInsert(event) : false;
  return formatPhoneInput(rawValue, { fromBulkInsert });
}

export function isPhoneComplete(value: string): boolean {
  return normalizePhoneDigits(value).length === 11;
}

export function createEmptyOtpDigits(length = PHONE_OTP_LENGTH): string[] {
  return Array.from({ length }, () => "");
}

export function sanitizeOtpInput(value: string): string {
  return value.replace(/\D/g, "").slice(-1);
}

export function sanitizeOtpPaste(value: string, length = PHONE_OTP_LENGTH): string[] {
  return value.replace(/\D/g, "").slice(0, length).split("");
}

/** Раскладывает код из autofill / paste по ячейкам OTP. */
export function otpDigitsFromBulkValue(value: string, length = PHONE_OTP_LENGTH): string[] {
  const nextDigits = createEmptyOtpDigits(length);
  sanitizeOtpPaste(value, length).forEach((digit, index) => {
    nextDigits[index] = digit;
  });
  return nextDigits;
}

export function joinOtpDigits(digits: string[]): string {
  return digits.join("");
}
